import { useImperativeHandle, useRef, useState } from 'react';
import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { NgButton, NgModal } from '@/ui';
import type { NgModalHandle } from '@/ui';
import { DeviceOs } from '@/signalr';
import { deviceOsName } from '../model/deviceOs';

const TOTAL_STEPS = 5;

export type HowToTurnOnGpsDialogHandle = {
  show: (deviceOs: DeviceOs) => void;
};

export type HowToTurnOnGpsDialogProps = {
  ref?: Ref<HowToTurnOnGpsDialogHandle>;
};

export function HowToTurnOnGpsDialog({ ref }: HowToTurnOnGpsDialogProps) {
  const { t } = useTranslation();
  const modalRef = useRef<NgModalHandle>(null);

  const [deviceOs, setDeviceOs] = useState<DeviceOs>(DeviceOs.Unknown);
  const [currentStep, setCurrentStep] = useState(1);

  useImperativeHandle(ref, () => ({
    show: (os: DeviceOs) => {
      setDeviceOs(os);
      setCurrentStep(1);
      modalRef.current?.show();
    },
  }));

  const closeDialog = () => {
    modalRef.current?.hide();
  };

  const showPreviousStep = () => {
    setCurrentStep((step) => (step > 1 ? step - 1 : step));
  };

  const showNextStep = () => {
    setCurrentStep((step) => (step < TOTAL_STEPS ? step + 1 : step));
  };

  const dialogHeader = t('HowToTurnGpsDialog_Title', { arg0: deviceOsName(deviceOs) });

  return (
    <NgModal
      ref={modalRef}
      header={dialogHeader}
      bodyTemplate={
        <div className="d-flex flex-column gap-2">
          <div className="d-flex gap-1 align-items-center justify-content-center">
            <NgButton icon="fal fa-arrow-left" disabled={currentStep === 1} onClick={showPreviousStep} />

            <img
              src={`Images/Incident/HowToTurnGpsDialog/${deviceOsName(deviceOs)}-${currentStep}.jpg`}
              alt="How to turn on GPS"
              className="img-fluid"
              style={{ maxHeight: '450px' }}
            />

            <NgButton icon="fal fa-arrow-right" disabled={currentStep === TOTAL_STEPS} onClick={showNextStep} />
          </div>

          <div className="text-center">
            {t('HowToTurnGpsDialog_StepCount', { arg0: currentStep, arg1: TOTAL_STEPS })}
          </div>
        </div>
      }
      footerTemplate={<NgButton text={t('Button_Close')} outline onClick={closeDialog} />}
    />
  );
}
